import moment from "moment"
import * as React from "react"
import { useLocation } from "react-router-dom"
import { t } from "ttag"
import { IconButton, Illustration, InformationMessage } from "@humanpredictiveintelligence/myqvt-library"

import { useUserPermissions } from "features/Permissions"
import { NotificationContext } from "features/Notification"
import { APPLICATION_URL } from "features/Navigation"
import { withRouteGuard } from "features/Navigation/withRouteGuard"
import { ExcelExportType } from "models/ExcelExportType"
import { ScheduledSurveyResultsPageSection } from "models/ScheduledSurveyResultsPageSection"
import { USER_ATTRIBUTE_VALUE } from "models/UserAttributeValue"
import {
  CommentWrittenAs,
  Supported_Language_Code as SupportedLanguageCode,
  UserPermissionCode,
} from "models/generated"
import { USER_ATTRIBUTE_SERVICE } from "services/UserAttributeService"
import illustrationDetective from "assets/images/illustration_detective.png"
import {
  ExcelExportDialog,
  ExcelExportDialogForm,
  ExcelExportDialogProps,
  ExportDialogForm,
  QuestionResultsUserAttributeValue,
  ScheduledSurveyFilters,
  ScheduledSurveyQuestionResults,
  ScheduledSurveyResultSectionSkeleton,
  ScheduledSurveyResultsHeaderSkeleton,
} from "./components"
import business from "config/business"
import * as Styles from "./ScheduledSurveyResults.styles"

import { useExportCommentsLazyQuery } from "graphql/queries/generated/ExportComments"
import { useExportParticipationsLazyQuery } from "graphql/queries/generated/ExportParticipations"
import { useExportRankingLazyQuery } from "graphql/queries/generated/ExportRanking"
import { usePublishScheduledSurveyResultsMutation } from "graphql/mutations/generated/PublishScheduledSurveyResults"
import {
  ScheduledSurveyGlobalResultsQuery,
  useScheduledSurveyGlobalResultsQuery,
} from "graphql/queries/generated/ScheduledSurveyGlobalResults"
import { useExportResponsesLazyQuery } from "graphql/queries/generated/ExportResponses"
import { useExportPowerPointLazyQuery } from "graphql/queries/generated/ExportPowerPoint"
import { useSurveyContentType } from "features/SurveyContentType"
import { useDelayedQuestionsQueries } from "./hooks/useDelayedQuestionsQueries"

const ScheduledSurveyResultsPageComponent: React.FC<ScheduledSurveyResultsPageProps> = props => {
  const notification = React.useContext(NotificationContext)
  const pageDivRef = React.createRef<HTMLDivElement>()
  const { isSpeakupSurvey } = useSurveyContentType()

  const isUserAllowed = useUserPermissions()
  const isAllowedToReadSurveyFeedback = isUserAllowed(UserPermissionCode.SurveyFeedbacksRead)
    || isUserAllowed(UserPermissionCode.LocalSurveyFeedbacksRead)
  const isAllowedToReadSurveyComments = isUserAllowed(UserPermissionCode.CommentsRead)

  //#region Get the target section from URL
  const scrollTargetSectionRef = React.createRef<HTMLDivElement>()
  const location = useLocation()
  const urlSearch = new URLSearchParams(location.search)
  const targetSection = urlSearch.get("section")
  const commentToFocus = parseInt(urlSearch.get("comment") || "", 10)
  //#endregion

  const {
    queueQuestions,
    onQuestionFetched,
    questionsBeingFetched,
  } = useDelayedQuestionsQueries(business.results.questionsNumberLimitToLoadConcurrently)

  const [ results, setResults ] = React.useState<ScheduledSurveyResultsState>()
  const [ selectedFilters, setSelectedFilters ] = React.useState<ScheduledSurveyFilters>(
    { userAttributesValues: new Map<number, USER_ATTRIBUTE_VALUE[]>(), userGroupsUuids: [] },
  )

  const filteringAttributeValueIds = selectedAttributeValueIds(selectedFilters.userAttributesValues)
  const [
    firstInteractedHierarchicalAttributeId,
    setFirstInteractedHierarchicalAttributeId,
  ] = React.useState<number | undefined>(undefined)

  const [ isExcelExportDialogVisible, setExcelExportDialogVisible ] = React.useState(false)
  const [
    excelExportDialogStatus,
    setExcelExportDialogStatus,
  ] = React.useState<ExcelExportDialogProps["status"]>("form")

  const [ isPowerPointExportDialogVisible, setPowerPointExportDialogVisible ] = React.useState(false)
  const [
    powerPointExportDialogStatus,
    setPowerPointExportDialogStatus,
  ] = React.useState<ExcelExportDialogProps["status"]>("form")

  const {
    loading: isLoadingResults,
    error: resultsQueryError,
    refetch: refetchResults,
  } = useScheduledSurveyGlobalResultsQuery({
    onCompleted(data) {
      if (!data.scheduledSurvey) {
        return
      }

      const publishingDate = data.scheduledSurvey.statistics_available_at

      setResults({
        areResultsPublished: !!publishingDate && moment().isSameOrAfter(publishingDate),
        publishingDate,
        scheduledSurvey: data.scheduledSurvey,
        statistics: data.scheduledSurvey.statistics,
      })
      queueQuestions(data.scheduledSurvey.questions.map(question => question.id))
    },
    variables: {
      filterAttributeIds: filteringAttributeValueIds.length ? filteringAttributeValueIds : undefined,
      id: props.id,
      userGroupsUuids: selectedFilters.userGroupsUuids.length ? selectedFilters.userGroupsUuids : undefined,
      writtenAs: CommentWrittenAs.Admin,
    },
  })

  const [ publishResults ] = usePublishScheduledSurveyResultsMutation({
    onCompleted() {
      refetchResults()
    },
    variables: {
      id: props.id,
    },
  })

  const [ exportComments ] = useExportCommentsLazyQuery({
    fetchPolicy: "network-only",
    onError: (error) => {
      notification.show(t`Failure`, error.graphQLErrors?.[0].message, "danger")
    },
  })

  const [ exportResponses ] = useExportResponsesLazyQuery({
    fetchPolicy: "network-only",
    onError: (error) => {
      notification.show(t`Failure`, error.graphQLErrors?.[0].message, "danger")
    },
  })

  const [ exportParticipations ] = useExportParticipationsLazyQuery({
    fetchPolicy: "network-only",
    onError: (error) => {
      notification.show(t`Failure`, error.graphQLErrors?.[0].message, "danger")
    },
  })

  const [ exportRanking ] = useExportRankingLazyQuery({
    fetchPolicy: "network-only",
    onError: (error) => {
      notification.show(t`Failure`, error.graphQLErrors?.[0].message, "danger")
    },
  })

  const [ exportPowerPoint ] = useExportPowerPointLazyQuery({
    fetchPolicy: "network-only",
    onError: (error) => {
      notification.show(t`Failure`, error.graphQLErrors?.[0].message, "danger")
    },
  })

  /** Attributes of first hierarchy level used to define local feedback attributes */
  const localFeedbacksAttributeValues = mainHierarchyHeadAttributeValues(results)

  React.useEffect(() => {
    setSelectedFilters({ userAttributesValues: new Map(), userGroupsUuids: [] })
  }, [ props.id ])

  React.useEffect(() => {
    if (scrollTargetSectionRef?.current && !isLoadingResults) {
      scrollTargetSectionRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }, [ scrollTargetSectionRef, isLoadingResults ])

  return (
    <Styles.Container
      $isClusterViolated={!results?.statistics.isClusterUnviolated}
      ref={pageDivRef}
      aria-label={t`Survey results page`}
    >
      {isLoadingResults && (
        <Styles.Loader>
          <ScheduledSurveyResultsHeaderSkeleton/>
          <ScheduledSurveyResultSectionSkeleton isLoading/>
          <ScheduledSurveyResultSectionSkeleton isLoading/>
        </Styles.Loader>
      )}
      {!isLoadingResults && resultsQueryError && (
        <InformationMessage
          variant="warning"
          text={t`An unexpected error happened while fetching the results. Please try again or contact support.`}
        />
      )}
      {areResultsLoaded(results, isLoadingResults) && !results.statistics.isClusterUnviolated && (
        <>
          {isExcelExportDialogVisible && (
            <ExcelExportDialog
              status={excelExportDialogStatus}
              filters={Array.from(selectedFilters.userAttributesValues.values()).flat()}
              onCloseRequested={closeAndResetExportDialog}
              onExportRequested={(requestExport) => requestExcelExport(requestExport, results)}
            />
          )}
          {isPowerPointExportDialogVisible && (
            <ExcelExportDialog
              variant="powerpoint"
              status={powerPointExportDialogStatus}
              filters={Array.from(selectedFilters.userAttributesValues.values()).flat()}
              onCloseRequested={closeAndResetPowerPointExportDialog}
              onExportRequested={() => triggerExportPowerPoint(results)}
            />
          )}
          <Styles.Header
            scheduling={results.scheduledSurvey}
            survey={results.scheduledSurvey.survey}
            surveyTheme={results.scheduledSurvey.survey.theme}
            userGroups={results.scheduledSurvey.userGroups}
            participationStatistics={results.statistics}
            onExportResults={() => window.print()}
            onExportExcel={showExcelExportDialog}
            onExportPowerPoint={() => showPowerPointExportDialog(results)}
            isExportMenuHidden
            $withRoundedBorders
          />
          <Illustration
            title={t`Oops, this survey did not receive enough participations.`}
            illustration={illustrationDetective}
          >
            <p>{t`To guarantee the anonymity of your collaborators, you cannot view these results.`}</p>
            <Styles.Hint>
              (<Styles.HintIcon name="face" size={24}/>
              {t`Add collaborators to the recipients list`})
            </Styles.Hint>
          </Illustration>
        </>
      )}
      {areResultsLoaded(results, isLoadingResults) && results.statistics.isClusterUnviolated && (
        <>
          {isExcelExportDialogVisible && (
            <ExcelExportDialog
              status={excelExportDialogStatus}
              filters={Array.from(selectedFilters.userAttributesValues.values()).flat()}
              attributes={USER_ATTRIBUTE_SERVICE.attributesFromAttributeValueStatistics(results.statistics.attributes)}
              onCloseRequested={closeAndResetExportDialog}
              onExportRequested={(exportRequest) => requestExcelExport(exportRequest, results)}
            />
          )}
          {isPowerPointExportDialogVisible && (
            <ExcelExportDialog
              variant="powerpoint"
              status={powerPointExportDialogStatus}
              filters={Array.from(selectedFilters.userAttributesValues.values()).flat()}
              attributes={USER_ATTRIBUTE_SERVICE.attributesFromAttributeValueStatistics(results.statistics.attributes)}
              onCloseRequested={closeAndResetPowerPointExportDialog}
              onExportRequested={() => triggerExportPowerPoint(results)}
            />
          )}
          <Styles.Header
            scheduling={results.scheduledSurvey}
            survey={results.scheduledSurvey.survey}
            surveyTheme={results.scheduledSurvey.survey.theme}
            userGroups={results.scheduledSurvey.userGroups}
            participationStatistics={results.scheduledSurvey.statistics}
            onExportResults={() => window.print()}
            onExportExcel={showExcelExportDialog}
            onExportPowerPoint={() => showPowerPointExportDialog(results)}
          />
          {results.statistics.attributes.length > 0 && (
            <Styles.AttributesFilters
              attributes={results.statistics.attributes}
              statisticsUserGroups={results.statistics.userGroups}
              defaultSelection={selectedFilters}
              onFilter={(selectedAttributeFilters) => {
                const selectedAttributeFiltersMap = new Map()
                results.scheduledSurvey?.questions.forEach((_, index) => {
                  selectedAttributeFiltersMap.set(index + 1, true)
                })
                setSelectedFilters(selectedAttributeFilters)
              }}
              scheduledSurveyId={results.scheduledSurvey.schedulingId}
              firstInteractedHierarchicalAttributeId={firstInteractedHierarchicalAttributeId}
              onHierarchicalFilterFirstInteraction={setFirstInteractedHierarchicalAttributeId}
            />
          )}
          <Styles.Questions>
            {results.scheduledSurvey?.questions.map((question, index) => {
              const isSectionTarget
                = targetSection === ScheduledSurveyResultsPageSection.QuestionPrefix + question.id

              return (
                <div
                  key={question.id}
                  ref={isSectionTarget ? scrollTargetSectionRef : undefined}
                >
                  <ScheduledSurveyQuestionResults
                    scheduledSurveyId={results.scheduledSurvey.schedulingId}
                    questionNumber={index + 1}
                    referenceQuestionId={question.id}
                    areResultsPublished={results.areResultsPublished}
                    publishingDate={results.publishingDate}
                    userAttributesValues={userAttributesValuesForQuestionResults(
                      results,
                      selectedFilters.userAttributesValues,
                    )}
                    userGroupsUuids={selectedFilters.userGroupsUuids}
                    focusCommentId={isSectionTarget ? commentToFocus : undefined}
                    doesQuestionsNeedFetching={questionsBeingFetched.has(question.id)}
                    onQuestionResponsesFetched={() => onQuestionFetched(question.id)}
                  />
                </div>
              )
            })}
          </Styles.Questions>

          {isAllowedToReadSurveyComments && (
            <div
              ref={targetSection === ScheduledSurveyResultsPageSection.CollaboratorsFeedback
                ? scrollTargetSectionRef
                : undefined}
            >
              <Styles.CollaboratorsFeedbackSection
                scheduledSurveyId={results.scheduledSurvey.schedulingId}
                attributes={filteringAttributeValueIds.length ? filteringAttributeValueIds : undefined}
              />
            </div>
          )}

          <Styles.SectionSeparator/>

          {isAllowedToReadSurveyFeedback && (
            <Styles.FeedbackSection
              scheduledSurveyId={results.scheduledSurvey.schedulingId}
              globalComment={results.scheduledSurvey?.comments?.[0]}
              scopedComments={results.scheduledSurvey?.scopedComments}
              areResultsPublished={results.areResultsPublished}
              publishingDate={results.publishingDate}
              onSaved={refetchResults}
              onDeleted={refetchResults}
              firstLevelAttributes={localFeedbacksAttributeValues}
            />
          )}

          {isSpeakupSurvey && (
            <Styles.PublishingStatus
              publishingDate={results.publishingDate}
              onTimeout={refetchResults}
              numberOfRecipients={results.statistics ? results.statistics.recipientsCount : 0}
              onPublish={publishResults}
              isManualPublication={results.scheduledSurvey.isManualPublication}
            />
          )}
          <Styles.Footer>
            <IconButton size="extrabig" icon="arrow_upward" isInverted onClick={scrollToTop}/>
          </Styles.Footer>
        </>
      )}
    </Styles.Container>
  )

  function areResultsLoaded(
    results: ScheduledSurveyResultsState | undefined,
    areResultsLoading: boolean,
  ): results is ScheduledSurveyResultsState {
    if (areResultsLoading) {
      return false
    }

    return !!results?.scheduledSurvey && !!results.statistics
  }

  /**
   * Show the Excel export dialog
   */
  function showExcelExportDialog() {
    setExcelExportDialogVisible(true)
  }

  /**
   * Hide the Excel export dialog and reset its status to "form"
   */
  function closeAndResetExportDialog() {
    setExcelExportDialogVisible(false)
    setExcelExportDialogStatus("form")
  }

  /**
   * Send request to export comments and save the result as a file
   */
  function requestExcelExport(exportRequest: ExportDialogForm, results: ScheduledSurveyResultsState): void {
    const excelRequest = exportRequest as ExcelExportDialogForm
    const variables = {
      attributeValueIds: filteringAttributeValueIds,
      language: excelRequest.language as SupportedLanguageCode,
      scheduledSurveyId: results.scheduledSurvey.schedulingId,
    }

    switch (excelRequest.exportType) {
      case ExcelExportType.Comments:
        exportComments({ variables })
        break
      case ExcelExportType.Responses:
        exportResponses({ variables })
        break
      case ExcelExportType.Participation:
        exportParticipations({ variables })
        break
      case ExcelExportType.Ranking:
        exportRanking({ variables })
        break
    }

    setExcelExportDialogStatus("confirmation")
  }

  /**
   * Show the PowerPoint export dialog
   * If no filters are available, skip and export immediately.
   */
  function showPowerPointExportDialog(results: ScheduledSurveyResultsState) {
    setPowerPointExportDialogVisible(true)

    const attributes = results.statistics.isClusterUnviolated
      ? USER_ATTRIBUTE_SERVICE.attributesFromAttributeValueStatistics(results.statistics.attributes)
      : []

    if (attributes.length === 0) {
      triggerExportPowerPoint(results)
    }
  }

  /**
   * Hide the PowerPoint export dialog and reset its status to "form"
   */
  function closeAndResetPowerPointExportDialog() {
    setPowerPointExportDialogVisible(false)
    setPowerPointExportDialogStatus("form")
  }

  /**
   * Trigger the PowerPoint export request and move the dialog to the confirmation step
   */
  function triggerExportPowerPoint(results: ScheduledSurveyResultsState): void {
    const variables = {
      attributeValueIds: filteringAttributeValueIds,
      scheduledSurveyId: results.scheduledSurvey.schedulingId,
      userGroupUuids: results.scheduledSurvey.userGroups.map(userGroup => userGroup.uuid),
    }

    exportPowerPoint({ variables })
    setPowerPointExportDialogStatus("confirmation")
  }

  /**
   * Coalesce the attribute selection object to a collection of selected attribute value IDs
   * @param selectedAttributes Attribute values selection
   */
  function selectedAttributeValueIds(selectedAttributes: Map<number, USER_ATTRIBUTE_VALUE[]>): number[] {
    return Array.from(selectedAttributes.values()).flatMap(values => values.map(value => value.id))
  }

  /**
   * Convert all attributes values into USER_ATTRIBUTE_VALUE
   * And add boolean info on selected status and hierarchical status
   */
  function userAttributesValuesForQuestionResults(
    results: ScheduledSurveyResultsState,
    selectedAttributes: Map<number, USER_ATTRIBUTE_VALUE[]>,
  ): QuestionResultsUserAttributeValue[] {
    return results.statistics.attributes.map((attributeStatistic) => {
      const convertedAttributeValue: QuestionResultsUserAttributeValue
        = USER_ATTRIBUTE_VALUE.fromSkinnyUserAttributeValue(attributeStatistic.attribute, attributeStatistic.usersCount)
      convertedAttributeValue.isSelected = selectedAttributeValueIds(selectedAttributes).find((attributeValueId) => {
        return attributeValueId === attributeStatistic.attribute.valueId
      }) !== undefined
      convertedAttributeValue.isMainHierarchyHead = attributeStatistic.attribute.isMainHierarchyHead

      return convertedAttributeValue
    }) || []
  }

  function mainHierarchyHeadAttributeValues(results?: ScheduledSurveyResultsState): USER_ATTRIBUTE_VALUE[] {
    const firstLevelAttributeValues = results?.statistics.attributes.reduce((accumulator, attributeStatistic) => {
      if (attributeStatistic.attribute.isMainHierarchyHead) {
        accumulator.push(USER_ATTRIBUTE_VALUE.fromSkinnyUserAttributeValue(
          attributeStatistic.attribute, attributeStatistic.usersCount,
        ))
      }

      return accumulator
    }, [] as USER_ATTRIBUTE_VALUE[]) || []

    const firstLevelAttributeId = firstLevelAttributeValues[0]?.attributeId

    const selectedFirstLevelAttributeValues = selectedFilters.userAttributesValues.get(firstLevelAttributeId)

    if (selectedFirstLevelAttributeValues?.length) {
      return selectedFilters.userAttributesValues.get(firstLevelAttributeId)!
    }

    return firstLevelAttributeValues
  }

  /**
   * Smoothly scroll to the top of the page
   */
  function scrollToTop(): void {
    if (pageDivRef.current) {
      pageDivRef.current.scrollTo({
        behavior: "smooth",
        top: 0,
      })
    }
  }
}

interface ScheduledSurveyResultsState {
  scheduledSurvey: NonNullable<ScheduledSurveyGlobalResultsQuery["scheduledSurvey"]>,
  statistics: NonNullable<ScheduledSurveyGlobalResultsQuery["scheduledSurvey"]>["statistics"],
  areResultsPublished: boolean,
  publishingDate?: string,
}

interface ScheduledSurveyResultsPageProps {
  id: number,
}

export const ScheduledSurveyResults = withRouteGuard(
  ScheduledSurveyResultsPageComponent,
  APPLICATION_URL.speakupScheduledSurveys(),
  (_, isUserAllowed) => !isUserAllowed(UserPermissionCode.ResultsRead),
)
